import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BuildWithUs.css';

const demoData = [
  { image: "/scene_start-sport_running.png", prompt: "How did I manage my pace through the run?" },
  { image: "/scene_start-sports_karting.png", prompt: "Did I brake too early into the turn, and what segments of the track did I lose time on?" },
  { image: "/scene_start-experiment_pendulum.png", prompt: "Analyze the harmonic oscillations and compute the frequency and decay parameters for me" },
  { image: "/scene_start-sports_chess.png", prompt: "Give me the full PGN of the game and would you send it to chess.com for analysis?" }
];

export default function BuildWithUs() {
  const [typedText, setTypedText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Mobile Mockup Animation State Machine
  useEffect(() => {
    let timeoutId;
    let isCancelled = false;
    
    const runAnimation = async () => {
      while (!isCancelled) {
        const currentPrompt = demoData[promptIndex].prompt;

        await new Promise(r => setTimeout(r, 1000)); // wait 1s after new image arrives
        
        // 1. Type text
        for (let i = 0; i <= currentPrompt.length; i++) {
          if (isCancelled) return;
          setTypedText(currentPrompt.slice(0, i));
          await new Promise(r => setTimeout(r, 60)); // typing speed
        }
        
        await new Promise(r => setTimeout(r, 600)); // wait before record
        if (isCancelled) return;

        // 2. Start recording
        setIsRecording(true);
        await new Promise(r => setTimeout(r, 2000)); // record duration
        if (isCancelled) return;
        
        // 3. Stop recording
        setIsRecording(false);
        await new Promise(r => setTimeout(r, 1000)); // wait before delete
        if (isCancelled) return;

        // 4. Delete text
        for (let i = currentPrompt.length; i >= 0; i--) {
          if (isCancelled) return;
          setTypedText(currentPrompt.slice(0, i));
          await new Promise(r => setTimeout(r, 15)); // delete speed
        }
        
        await new Promise(r => setTimeout(r, 500)); // wait before next
        
        if (isCancelled) return;
        setPromptIndex(prev => (prev + 1) % demoData.length);
      }
    };

    runAnimation();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [promptIndex]);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg("Please enter a valid email address format.");
      return;
    }
    setErrorMsg('');
    
    const subject = "BayesMech Vision Early Access Partners: Signing Up";
    const body = `Hi, ${email} wants to partner with us for the early access program and help us build bayesmech. I thought we'd get you chatting with our founding team first. The app should be downloadable from play store (by the name BayesMech Vision), but it's in early stage and a lot of features don't work. Till then, feel free to share ideas with the team. And thank you ${email} very much for signing up.`;
    
    window.location.href = `mailto:animesh@bayesmech.com?cc=${encodeURIComponent(email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setEmail('');
  };

  return (
    <section className="build-section" id="build">
      <div className="build__container">
        
        {/* Left Side: Form */}
        <div className="build__info">
          <h2 className="build__heading">Help us Build</h2>
          <p className="build__subheading">
            Excited about building the future of Visual Intelligence with us?
            Sign up below to get early access to apps and APIs and help us build for your use-cases.
          </p>
          <p className="build__subheading">
            If you'd like to chat or have ideas, we'd love to hear from at <a href="mailto:animesh@bayesmech.com">animesh@bayesmech.com</a>.
          </p>
          
          <form className="build__form" onSubmit={handleEmailSubmit} noValidate>
            <input 
              type="email" 
              placeholder="Your email address" 
              className="build__input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMsg) setErrorMsg('');
              }}
            />
            <button type="submit" className="build__btn">Join Early Access</button>
            {errorMsg && <div className="build__error">{errorMsg}</div>}
          </form>
        </div>

        {/* Right Side: Mobile App Demo */}
        <div className="build__demo">
          <div className="build__demo-label">TRY OUT OUR APP</div>
          <div className="build__mobile-frame">
            {/* Status Bar */}
            <div className="build__mobile-status">
              <span>9:41 AM</span>
              <div className="build__mobile-status-icons">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20h20V4L2 20z"/></svg>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01"/></svg>
                <svg width="16" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="16" height="10" rx="2" ry="2"/><line x1="22" y1="11" x2="22" y2="13"/></svg>
              </div>
            </div>
            
            {/* App Content: Camera Screen */}
            <div className="build__mobile-app">
              {/* Top Half - Camera View */}
              <div className="build__mobile-camera">
                <div className="build__mobile-camera-inner">
                  <AnimatePresence initial={false}>
                    <motion.img 
                      key={promptIndex}
                      src={demoData[promptIndex].image} 
                      alt="Camera feed" 
                      className="build__mobile-camera-feed"
                      initial={{ x: '100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '-100%' }}
                      transition={{ type: 'tween', ease: 'easeInOut', duration: 0.5 }}
                    />
                  </AnimatePresence>
                </div>
                {isRecording && (
                  <div className="build__mobile-live-badge">● REC</div>
                )}
              </div>

              {/* Bottom Half - Chat UI */}
              <div className="build__mobile-chat">
                <textarea 
                  className="build__mobile-textarea"
                  value={typedText}
                  readOnly
                  placeholder="Type your message here..."
                />
                
                {/* Floating buttons inside the textarea */}
                <div className="build__mobile-help-btn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                
                <div className="build__mobile-action-right">
                  <div className="build__mobile-action-btn">
                    {/* Mic Icon */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                  </div>
                  <div className={`build__mobile-send-btn ${typedText.length > 0 ? 'active' : ''}`}>
                    {/* Send Icon */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </div>
                </div>
              </div>

              {/* Central Record Button */}
              <div className={`build__mobile-record-btn ${isRecording ? 'recording' : ''}`}>
                <div className="build__mobile-record-inner"></div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="build__mobile-nav">
              <div className="build__mobile-nav-bar"></div>
              <div className="build__mobile-nav-btn active">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
              </div>
              <div className="build__mobile-nav-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"/><polyline points="14 2 14 8 20 8"/><path d="M2 15h10"/><path d="M2 21v-6"/><path d="M12 15v6"/></svg>
              </div>
              <div className="build__mobile-nav-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </div>
            </div>
            {/* Home Indicator */}
            <div className="build__mobile-home-indicator"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
